import { Filter } from './Filter';
import { AccountParams } from './AccountParams';
import { Audience, getAudience, getPreviousAudience } from './Audience';
import { Interaction, getInteraction, getPreviousInteraction } from './Interaction';
import { Occupancy, getLiveOccupancy, getPreviousLiveOccupancy, getFootfall, getPreviousFootfall, getCrossings, getPreviousCrossings, hasValues } from './Occupancy';
import { getCategories } from './Collapsed';
import { MEDIA_PAGE_SIZE, getMediaPlaybackPage } from './MediaPlayback';
import { Sales, getProductSales, getPreviousProductSales } from './ProductSales';
import { ISaleService } from '../services/ISaleService';

// Where the dashboard cards get their data
interface DashboardView {
  setAudience(audience: Audience): void,
  setPreviousAudience(audience: Audience): void,
  setCategories(categories: string[]): void,
  setInteraction(interaction: Interaction): void,
  setPreviousInteraction(interaction: Interaction): void,
  setOccupancy(occupancy: Occupancy): void,
  setPreviousOccupancy(occupancy: Occupancy): void,
  setFootfall(footfall: Occupancy): void,
  setPreviousFootfall(footfall: Occupancy): void,
  setCrossings(crossings: Occupancy): void,
  setPreviousCrossings(crossings: Occupancy): void,
  setDisplayMediaPlayback(display: boolean): void,
  setProductSales(sales: Sales): void,
  setPreviousProductSales(sales: Sales): void,
  setDisplayProductSales(display: boolean): void,
  // called when the current period of every card is loaded, before the previous period
  currentLoaded?(): void
}

const dashboardApi = {
  getAudience, getPreviousAudience,
  getCategories,
  getInteraction, getPreviousInteraction,
  getLiveOccupancy, getPreviousLiveOccupancy,
  getFootfall, getPreviousFootfall,
  getCrossings, getPreviousCrossings,
  getMediaPlaybackPage,
  getProductSales, getPreviousProductSales
};

type DashboardApi = typeof dashboardApi;

const emptySales = (): Sales => ({ productSales: [], familySales: [], brandSales: [], productSalesAmount: 0, productSalesUnits: 0, familySalesAmount: 0, familySalesUnits: 0, brandSalesAmount: 0, brandSalesUnits: 0 });

// Shows a card as soon as its data arrives. A failing card is logged and does not stop the others.
const show = <T>(request: Promise<T>, set: (value: T) => void): Promise<T | undefined> => {
  return request.then(value => {
    set(value);
    return value;
  }).catch(error => {
    console.log(error);
    return undefined;
  });
}

/**
 * Starts the requests one tick apart and waits for all of them. The Cloud API sends together
 * the requests made in the same tick, so each one goes to the Studio in its own batch.
 */
const inSeparateBatches = async <T extends unknown[] | []>(requests: { [K in keyof T]: () => Promise<T[K]> }): Promise<T> => {
  const started: Promise<unknown>[] = [];
  for(const request of requests as (() => Promise<unknown>)[]) {
    started.push(request());
    // the batch of this request is sent before the next one starts
    await Promise.resolve();
  }
  return await Promise.all(started) as T;
}

/**
 * Loads every dashboard card: first the current period of all the cards and then, with it on
 * screen, the previous period. Each card goes to the Studio in its own batch request.
 * @param saleService Sale service, or null when the sales module is off.
 */
const loadDashboard = async (filter: Filter, params: AccountParams, saleService: ISaleService | null, view: DashboardView, api: DashboardApi = dashboardApi): Promise<void> => {
  if(!saleService) {
    view.setProductSales(emptySales());
    view.setDisplayProductSales(false);
  }
  const [occupancy, footfall, crossings, sales] = await inSeparateBatches([
    () => show(api.getLiveOccupancy(filter, params), view.setOccupancy),
    () => show(api.getFootfall(filter, params), view.setFootfall),
    () => show(api.getCrossings(filter, params), view.setCrossings),
    () => saleService ? show(api.getProductSales(filter, saleService, params), productSales => {
      view.setDisplayProductSales(productSales.productSales.length > 0 || productSales.familySales.length > 0);
      view.setProductSales(productSales);
    }) : Promise.resolve(undefined),
    () => show(api.getAudience(filter, params), view.setAudience),
    () => show(api.getCategories(filter, params), view.setCategories),
    () => show(api.getInteraction(filter, params), view.setInteraction),
    // the same first page the media playback card requests, so it reuses it
    () => show(api.getMediaPlaybackPage(filter, 0, MEDIA_PAGE_SIZE, '', params), mediaPlayback => view.setDisplayMediaPlayback(mediaPlayback.list.length > 0))
  ]);
  view.currentLoaded && view.currentLoaded();
  // previous occupancy, footfall and crossings only for cards with values in the current period
  await inSeparateBatches([
    () => show(api.getPreviousAudience(filter, params), view.setPreviousAudience),
    () => show(api.getPreviousInteraction(filter, params), view.setPreviousInteraction),
    () => occupancy && hasValues(occupancy) ? show(api.getPreviousLiveOccupancy(filter, params), view.setPreviousOccupancy) : Promise.resolve(undefined),
    () => footfall && hasValues(footfall) ? show(api.getPreviousFootfall(filter, params), view.setPreviousFootfall) : Promise.resolve(undefined),
    () => crossings && hasValues(crossings) ? show(api.getPreviousCrossings(filter, params), view.setPreviousCrossings) : Promise.resolve(undefined),
    () => saleService && sales ? show(api.getPreviousProductSales(filter, saleService, params), view.setPreviousProductSales) : Promise.resolve(undefined)
  ]);
}

export { DashboardView, DashboardApi, dashboardApi, loadDashboard };
